import type { Command } from './types';

/** Inclusive-exclusive `[start, end)` slice of the matched text. */
export type MatchRange = [number, number];

export interface FuzzyMatch {
  score: number;
  /** Ranges of `text` that matched, in order, merged where adjacent. */
  ranges: MatchRange[];
}

/**
 * Tier floors. A better *kind* of match always beats a worse one, and the contiguity /
 * word-boundary bonus (capped below the gap between tiers) orders results inside a tier.
 */
const TIER = { exact: 100, prefix: 88, wordPrefix: 72, substring: 56, subsequence: 8 } as const;
const MAX_BONUS = 11;

const SEPARATOR = /[\s/_\-.·:,()[\]]/;

function isBoundary(text: string, index: number): boolean {
  if (index === 0) return true;
  const prev = text.charAt(index - 1);
  if (SEPARATOR.test(prev)) return true;
  // camelCase / PascalCase boundary.
  return prev === prev.toLowerCase() && prev !== prev.toUpperCase() && text.charAt(index) !== text.charAt(index).toLowerCase();
}

function mergeRanges(indices: number[]): MatchRange[] {
  const out: MatchRange[] = [];
  for (const i of indices) {
    const last = out[out.length - 1];
    if (last && last[1] === i) last[1] = i + 1;
    else out.push([i, i + 1]);
  }
  return out;
}

/** Plain greedy subsequence: each query character takes the next occurrence. */
function greedyIndices(q: string, lower: string): number[] | null {
  const indices: number[] = [];
  let from = 0;
  for (let qi = 0; qi < q.length; qi += 1) {
    const next = lower.indexOf(q.charAt(qi), from);
    if (next < 0) return null;
    indices.push(next);
    from = next + 1;
  }
  return indices;
}

/**
 * Greedy match that prefers word-boundary positions ("gto" highlighting **G**o **t**o **o**rders
 * rather than G-o-t...), except where staying put continues a contiguous run. Falls back to the
 * plain greedy match when the boundary preference would strand a later character.
 */
function subsequenceIndices(q: string, lower: string, text: string): number[] | null {
  const plain = greedyIndices(q, lower);
  if (!plain) return null;
  const indices: number[] = [];
  let from = 0;
  for (let qi = 0; qi < q.length; qi += 1) {
    const ch = q.charAt(qi);
    const next = lower.indexOf(ch, from);
    if (next < 0) return plain;
    let chosen = next;
    const previous = indices[indices.length - 1];
    // Contiguity beats the boundary preference.
    if (previous == null || next !== previous + 1) {
      if (!isBoundary(text, next)) {
        for (let j = next + 1; j < lower.length; j += 1) {
          if (lower.charAt(j) === ch && isBoundary(text, j)) {
            chosen = j;
            break;
          }
        }
      }
    }
    indices.push(chosen);
    from = chosen + 1;
  }
  return indices.length === q.length ? indices : plain;
}

/** Contiguity + word-boundary bonus for a set of matched indices, normalised into `[0, MAX_BONUS]`. */
function bonusFor(indices: number[], text: string): number {
  if (indices.length === 0) return 0;
  let raw = 0;
  for (let i = 0; i < indices.length; i += 1) {
    const index = indices[i]!;
    if (isBoundary(text, index)) raw += 2;
    if (i > 0 && index === indices[i - 1]! + 1) raw += 3;
  }
  const max = indices.length * 5;
  return max === 0 ? 0 : (raw / max) * MAX_BONUS;
}

/**
 * Fuzzy-matches `query` against `text`, returning a score and the ranges that matched so the UI can
 * highlight them. Exact > prefix > word prefix > substring > subsequence, refined within each tier
 * by how contiguous the match is and how many characters landed on word boundaries.
 */
export function fuzzyMatch(query: string, text: string): FuzzyMatch | null {
  const q = query.trim().toLowerCase();
  if (!q) return { score: 1, ranges: [] };
  if (!text) return null;
  const lower = text.toLowerCase();

  if (lower === q) return { score: TIER.exact, ranges: [[0, text.length]] };
  if (lower.startsWith(q)) return { score: TIER.prefix + MAX_BONUS, ranges: [[0, q.length]] };

  // Word prefix: the query starts a word inside the text.
  for (let i = 1; i < lower.length; i += 1) {
    if (!isBoundary(text, i)) continue;
    if (lower.startsWith(q, i)) {
      const tail = 1 - i / Math.max(1, lower.length);
      return { score: TIER.wordPrefix + tail * MAX_BONUS, ranges: [[i, i + q.length]] };
    }
  }

  const at = lower.indexOf(q);
  if (at >= 0) {
    const tail = 1 - at / Math.max(1, lower.length);
    return { score: TIER.substring + tail * MAX_BONUS, ranges: [[at, at + q.length]] };
  }

  const indices = subsequenceIndices(q, lower, text);
  if (!indices) return null;
  return { score: TIER.subsequence + bonusFor(indices, text), ranges: mergeRanges(indices) };
}

/** Scores `text` against `query` (0 = no match). Exact > prefix > word prefix > substring > subsequence. */
export function scoreMatch(query: string, text: string): number {
  return fuzzyMatch(query, text)?.score ?? 0;
}

/** Ranges of `text` matched by `query`, or an empty array when there is no match. */
export function matchRanges(query: string, text: string): MatchRange[] {
  return fuzzyMatch(query, text)?.ranges ?? [];
}

/** Combined score for a command: label, keywords (weighted) and group (weakly). */
export function scoreCommand(query: string, command: Command): number {
  return matchCommand(query, command)?.score ?? 0;
}

export interface CommandMatch {
  score: number;
  /** Ranges within `command.label`, for highlighting. Empty when the hit came from a keyword. */
  ranges: MatchRange[];
}

/**
 * Best match for a command across its label, keywords, group and description. Navigation, record,
 * action and settings commands all rank through this one function — the group only nudges the score.
 */
export function matchCommand(query: string, command: Command): CommandMatch | null {
  const q = query.trim();
  if (!q) return { score: 1, ranges: [] };
  const label = fuzzyMatch(q, command.label);
  let best = label?.score ?? 0;
  for (const keyword of command.keywords ?? []) best = Math.max(best, scoreMatch(q, keyword) * 0.85);
  if (command.group) best = Math.max(best, scoreMatch(q, command.group) * 0.4);
  if (command.description) best = Math.max(best, scoreMatch(q, command.description) * 0.5);
  if (best <= 0) return null;
  return { score: best, ranges: label?.ranges ?? [] };
}

export interface RankedCommand {
  command: Command;
  score: number;
  /** Highlight ranges within the command label. */
  ranges?: MatchRange[];
}

/**
 * With nothing typed: one command from each group in turn, in the order the groups first appear.
 *
 * The window is `limit` wide and the Navigation group alone outgrew it once the product had a dozen areas,
 * so a plain `slice` showed screens and nothing else — the Actions the palette exists to surface never
 * reached the list until the operator typed. Round-robin keeps every group represented at any size.
 */
function sampleByGroup(commands: Command[], limit: number): Command[] {
  const buckets = new Map<string, Command[]>();
  for (const command of commands) {
    const key = command.group ?? '';
    const bucket = buckets.get(key);
    if (bucket) bucket.push(command);
    else buckets.set(key, [command]);
  }
  const lists = [...buckets.values()];
  const out: Command[] = [];
  for (let i = 0; out.length < limit && lists.some((list) => i < list.length); i += 1) {
    for (const list of lists) {
      if (out.length >= limit) break;
      if (i < list.length) out.push(list[i]!);
    }
  }
  return out;
}

/** Filters and sorts commands by score (stable for equal scores). */
export function rankCommands(query: string, commands: Command[], limit = 50): RankedCommand[] {
  const q = query.trim();
  if (!q) return sampleByGroup(commands, limit).map((command) => ({ command, score: 1, ranges: [] }));
  return commands
    .map((command) => ({ command, match: matchCommand(q, command) }))
    .filter((r): r is { command: Command; match: CommandMatch } => r.match != null)
    .map(({ command, match }) => ({ command, score: match.score, ranges: match.ranges }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export interface CommandGroup {
  label: string;
  commands: Command[];
  /** Same commands with their scores and highlight ranges. */
  entries: RankedCommand[];
}

/** Groups ranked commands preserving first-seen group order. */
export function groupCommands(ranked: RankedCommand[], fallbackGroup = 'Commands'): CommandGroup[] {
  const map = new Map<string, RankedCommand[]>();
  for (const r of ranked) {
    const key = r.command.group ?? fallbackGroup;
    const list = map.get(key);
    if (list) list.push(r);
    else map.set(key, [r]);
  }
  return Array.from(map.entries()).map(([label, entries]) => ({ label, entries, commands: entries.map((e) => e.command) }));
}
